/**
 * Feature Config Card Component
 *
 * Renders a card for a single configurable feature with dynamic form fields
 * based on the feature's option schema.
 */

import { type ReactElement, useState } from 'react';
import type { ConfigurableFeature } from '../schemas/feature.schemas';
import { useUpdateFeaturePreference } from '../hooks/useFeaturePreferences';
import { OptionFieldRenderer } from './OptionFieldRenderer';
import { autoClearDependents } from '../utils/autoClearDependents';

interface FeatureConfigCardProps {
  feature: ConfigurableFeature;
}

export function FeatureConfigCard({
  feature,
}: FeatureConfigCardProps): ReactElement {
  const [values, setValues] = useState<Record<string, unknown>>(
    () => ({ ...feature.current_values }),
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [autoClearNote, setAutoClearNote] = useState<string | null>(null);
  const updatePreference = useUpdateFeaturePreference();

  const handleChange = (key: string, value: unknown): void => {
    setSuccessMessage(null);
    setErrorMessage(null);

    const { values: next, note } = autoClearDependents(
      key, value, values, feature.configurable_options,
    );
    setAutoClearNote(note);
    setValues(next);
  };

  const handleSave = async (): Promise<void> => {
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      await updatePreference.mutateAsync({
        featureId: feature.feature_id,
        options: values,
      });
      setSuccessMessage('Preferences saved successfully');
    } catch {
      setErrorMessage('Failed to save preferences. Please try again.');
    }
  };

  return (
    <div className="feature-config-card">
      <div className="feature-config-card__header">
        <h3 className="feature-config-card__title">
          {feature.display_name}
        </h3>
        <span
          className={`feature-config-card__badge feature-config-card__badge--${feature.category}`}
        >
          {feature.category}
        </span>
      </div>

      {successMessage && (
        <div className="success-message">{successMessage}</div>
      )}
      {errorMessage && <div className="error-message">{errorMessage}</div>}
      {autoClearNote && (
        <div className="option-field__auto-clear-note" role="status">
          {autoClearNote}
        </div>
      )}

      <div className="feature-config-card__fields">
        <OptionFieldRenderer
          schema={feature.configurable_options}
          values={values}
          onChange={handleChange}
        />
      </div>

      <div className="form-actions">
        <button
          type="button"
          disabled={updatePreference.isPending}
          className="save-button"
          onClick={() => void handleSave()}
        >
          {updatePreference.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}
